/**
 * RFID reader control — talks to mttr/app_api `/api/boxes/{scu}/reader/*`.
 *
 * Two operations that are deliberately NOT the same thing:
 *
 *   - **Test scan**: the control center drives the reader itself over an SSH
 *     tunnel. Seconds, diagnostic only, publishes nothing, leaves the box's
 *     scan history alone.
 *   - **Force scan**: restarts the box's own scanner so it runs a real scan
 *     with production logic and publishes to the real MQTT topics. Takes
 *     5-10 MINUTES, so it returns a job to poll rather than a result.
 *
 * Both return a ReaderJob; the UI polls `fetchReaderJob` until `is_running`
 * goes false. Everything is gated on `operator.triage` server-side.
 *
 * **A SmartBox can carry two or three readers.** They sit on different
 * addresses, their antenna port numbers each restart at 1, and one can be
 * switched off at the box while the others scan. So `ReaderStatus.readers` is
 * a list, and every operation takes an optional `readerId` naming the one it
 * acts on. Omitting it targets the lowest-numbered ACTIVE reader — which is
 * what a single-reader box always got, and on a multi-reader box is a default
 * rather than "the reader", which is why every response echoes `reader_id`.
 */

import { apiFetch } from "./apiClient";

/** Lifecycle of a reader operation. Anything but the three terminal values
 *  means still in flight. */
export type ReaderJobStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "timed_out";

export type ReaderJobKind = "test_scan" | "force_scan";

/** Live progress during a force scan, scraped from the box's own scanner log. */
export interface ReaderJobProgress {
  scan_id?: number;
  elapsed_seconds?: number;
  /** Which walking round the box is on. */
  round?: number;
  /** Distinct tags seen so far this scan. */
  tags?: number;
  new_tags?: number;
  /** e.g. "2/3" — consecutive rounds that added nothing. The scan ends when
   *  this hits its limit, so it is a genuine "nearly done" signal. */
  zero_rounds?: string;
  /** Set while resting so EPC Gen2 S2 tag flags decay before the box scans
   *  again — skipping it makes the next scan under-read. */
  resting_seconds?: number;
  why?: string;
  state?: string;
}

export interface ReaderAntennaResult {
  antenna: number;
  unique_epcs: number;
  total_reads: number;
  /** The reader sent an end-of-round frame. Proves the READER answered — an
   *  unconnected port completes its round too, so this is not proof that an
   *  antenna is attached. */
  round_completed: boolean;
  seconds: number;
  error: string | null;
}

export interface ReaderJobResult {
  /** Which reader this outcome is about. On a multi-reader box a bare antenna
   *  number or power reading is ambiguous without it. */
  reader_id?: number;
  previous_ip?: string;
  /** Antenna update: which ports changed state, and the prior set so a
   *  stuck-firmware detection can be undone by hand. */
  activated?: number[];
  deactivated?: number[];
  previous_active?: number[];
  detected?: number[];
  note?: string;
  /** Power writes: what the ports read before the change, so the UI can
   *  offer "restore to N" — the only way back on this firmware. */
  before_dbm?: number[];
  after_dbm?: number[];
  set_to_dbm?: number;
  unique_tags?: number;
  total_reads?: number;
  seconds?: number;
  antennas?: ReaderAntennaResult[];
  epcs?: string[];
  /** Set when a scan finished but read nothing, explaining which case it is. */
  warning?: string;
  /** Force scan: the box's own scan row. */
  scan?: {
    scan_id: number;
    status: string;
    total_tags: number | null;
    antennas_used: number | null;
    duration_seconds: number | null;
    laps_completed: number | null;
    failure_reason: string | null;
  };
  /** Force scan: where the box published, one entry per topic. */
  published?: { kind: string; scan_id: string; broker: string; topic: string }[];
  rounds?: number;
  scan_seconds?: number;
}

export interface ReaderJob {
  job_id: number;
  scu_name: string;
  kind: ReaderJobKind | string;
  status: ReaderJobStatus;
  phase: string | null;
  progress: ReaderJobProgress | null;
  result: ReaderJobResult | null;
  error: string | null;
  box_scan_id: number | null;
  tags_found: number | null;
  /** Completed, but zero tags. NOT a failure — the reader answered and saw
   *  nothing. Server-computed so the UI never infers it from a bare 0. */
  read_nothing: boolean;
  is_running: boolean;
  requested_by: string | null;
  created_utc: string | null;
  updated_utc: string | null;
  ended_utc: string | null;
}

/** ONE reader on a box: its configuration, plus its live health when the box
 *  has it switched on.
 *
 *  Antenna numbers are scoped to this reader — port 4 on reader 1 and port 4
 *  on reader 2 are different physical antennas, so a port list is meaningless
 *  without the `reader_id` it came from. */
export interface ReaderInfo {
  reader_id: number;
  ip_address: string;
  port: number;
  antennas: number[];
  antenna_count: number;
  total_ports?: number | null;
  description?: string | null;
  /** The box has this reader but is NOT scanning it (`reader.is_active = 0`).
   *  One switch, not thirty-two dead antennas. A disabled reader is listed but
   *  deliberately never probed — nothing is dialling it, so failing to reach
   *  it is expected rather than a fault, and the liveness fields below are
   *  absent for it. */
  is_active: boolean;
  /** The TCP tunnel opened. Proves the bridge is alive, nothing more. */
  bridge_reachable?: boolean;
  /** The reader replied. This is the one that proves the reader is alive. */
  reader_responding?: boolean;
  firmware?: string;
  temperature_c?: number | null;
  detector_db?: number | null;
  power_dbm?: Record<string, number>;
  power_summary?: string;
  warnings?: string[];
  error?: string;
}

export interface ReaderStatus {
  scu_name: string;
  /** False when the box has no reader row — a real state, not an error. */
  configured: boolean;
  /** EVERY reader the box has, in reader_id order, including any it has
   *  switched off. This is the whole truth about the box. */
  readers?: ReaderInfo[];
  reader_count?: number;
  /** Server-computed so the UI never has to infer it from readers.length. */
  multi_reader?: boolean;

  /* --- compatibility shim: the pre-multi-reader flat shape, mirroring the
   * first ACTIVE reader. Kept so an older client keeps working; prefer
   * `readers` for anything new, because on a two- or three-reader box these
   * describe one reader and not the box. */
  reader_id?: number;
  ip_address?: string;
  port?: number;
  antennas?: number[];
  antenna_count?: number;
  total_ports?: number | null;
  description?: string | null;
  bridge_reachable?: boolean;
  reader_responding?: boolean;
  firmware?: string;
  temperature_c?: number | null;
  detector_db?: number | null;
  power_dbm?: Record<string, number>;
  power_summary?: string;
  warnings?: string[];
  error?: string;
  latest_job: ReaderJob | null;
}

/** The readers to show, tolerating a server that predates `readers[]`.
 *
 *  Older responses carry only the flat fields, so this reconstructs a
 *  one-element list from them rather than rendering an empty panel. */
export function readersOf(status: ReaderStatus | null): ReaderInfo[] {
  if (!status?.configured) return [];
  if (status.readers?.length) return status.readers;
  if (status.reader_id == null) return [];
  return [
    {
      reader_id: status.reader_id,
      ip_address: status.ip_address ?? "",
      port: status.port ?? 4001,
      antennas: status.antennas ?? [],
      antenna_count: status.antenna_count ?? status.antennas?.length ?? 0,
      total_ports: status.total_ports,
      description: status.description,
      is_active: true,
      bridge_reachable: status.bridge_reachable,
      reader_responding: status.reader_responding,
      firmware: status.firmware,
      temperature_c: status.temperature_c,
      detector_db: status.detector_db,
      power_dbm: status.power_dbm,
      power_summary: status.power_summary,
      warnings: status.warnings,
      error: status.error,
    },
  ];
}

/** One line naming a reader for a picker or a heading. */
export function describeReader(r: ReaderInfo): string {
  const off = r.is_active ? "" : " · disabled at the box";
  return `Reader ${r.reader_id} · ${r.ip_address} · ${r.antenna_count} antenna${
    r.antenna_count === 1 ? "" : "s"
  }${off}`;
}

const base = (scu: string): string => `/api/boxes/${encodeURIComponent(scu)}/reader`;

export async function fetchReaderStatus(
  token: string,
  scu: string,
): Promise<ReaderStatus> {
  return apiFetch<ReaderStatus>(base(scu), token);
}

/** Drive ONE reader and report what it read. `antennas` are that reader's
 *  own port numbers. */
export async function startTestScan(
  token: string,
  scu: string,
  antennas?: number[],
  readerId?: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/test-scan`, token, {
    method: "POST",
    body: JSON.stringify({
      ...(antennas && antennas.length ? { antennas } : {}),
      ...(readerId != null ? { reader_id: readerId } : {}),
    }),
  });
}

export async function startForceScan(
  token: string,
  scu: string,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/force-scan`, token, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function fetchReaderJob(
  token: string,
  scu: string,
  jobId: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/jobs/${jobId}`, token);
}

export async function fetchReaderJobs(
  token: string,
  scu: string,
  limit = 20,
): Promise<{ jobs: ReaderJob[] }> {
  return apiFetch<{ jobs: ReaderJob[] }>(`${base(scu)}/jobs?limit=${limit}`, token);
}

/** One line summarising a job for the UI, in plain operator language.
 *
 * Kept here rather than in the components so the map card and the ticket panel
 * cannot drift into describing the same outcome differently. */
export function describeJob(job: ReaderJob): string {
  if (job.is_running) {
    const p = job.progress;
    if (p?.round != null) {
      const zero = p.zero_rounds ? ` · ${p.zero_rounds} quiet rounds` : "";
      return `round ${p.round} · ${p.tags ?? 0} tags found${zero}`;
    }
    if (p?.elapsed_seconds != null) {
      return `${job.phase ?? "working"} · ${p.elapsed_seconds}s elapsed`;
    }
    return job.phase ?? "working…";
  }
  if (job.status === "completed") {
    if (job.read_nothing) return "completed — but read ZERO tags";
    const n = job.tags_found ?? 0;
    return `completed — ${n} tag${n === 1 ? "" : "s"} read`;
  }
  if (job.status === "timed_out") return "timed out";
  return job.error ? `failed — ${job.error}` : "failed";
}

/** Polling cadence. A force scan runs for minutes, so a slow poll is plenty
 *  and keeps the API quiet; a test scan finishes in seconds. */
export const JOB_POLL_MS = 3000;

/* ------------------------------------------------------------------ */
/* Hardware writes — ticket view only.                                 */
/*                                                                     */
/* Each is a single command against the reader and returns the job row */
/* it was recorded as, so the ticket history picks it up like a scan.  */
/* The server owns the safety rules; the UI mirrors them in wording    */
/* only, and a refusal comes back as a normal error to display.        */
/* ------------------------------------------------------------------ */

/** `reader_id` in a body, only when one was chosen — omitting it lets the
 *  server pick the lowest-numbered active reader. */
function withReader(readerId?: number): Record<string, number> {
  return readerId != null ? { reader_id: readerId } : {};
}

/** `?reader_id=` for the GET routes. */
function readerQuery(readerId?: number): string {
  return readerId != null ? `?reader_id=${readerId}` : "";
}

export interface BridgeDevice {
  mac: string;
  ip: string;
  name: string;
  /** False for something that merely heard the broadcast — a search crosses
   *  subnets, so the list can contain unrelated devices. */
  is_usr_bridge: boolean;
}

/** Set RF power on ONE reader's 32 ports.
 *
 *  EVERY power write persists: the reader's temporary-power command is not
 *  implemented on fleet firmware (measured), so a change survives reboots
 *  until it is set back. The server therefore always requires confirmation
 *  and returns `before_dbm` so we can offer a restore.
 *
 *  On a multi-reader box the other readers keep whatever power they had. */
export async function setReaderPower(
  token: string,
  scu: string,
  dbm: number,
  readerId?: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/power`, token, {
    method: "POST",
    body: JSON.stringify({ dbm, confirm: true, ...withReader(readerId) }),
  });
}

/** Antenna-detect (return-loss) threshold in dB, on ONE reader. Default 5. */
export async function setReaderDetector(
  token: string,
  scu: string,
  db: number,
  readerId?: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/detector`, token, {
    method: "POST",
    body: JSON.stringify({ db, ...withReader(readerId) }),
  });
}

/** Reset ONE reader — it stops answering for ~10-15s. The others keep
 *  scanning throughout. */
export async function resetReader(
  token: string,
  scu: string,
  readerId?: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/reset`, token, {
    method: "POST",
    body: JSON.stringify({ confirm: true, ...withReader(readerId) }),
  });
}

export interface BridgeDiscovery {
  devices: BridgeDevice[];
  count: number;
  /** The subnet the sweep was hinted at — the chosen reader's address. */
  hint_ip: string | null;
  reader_id?: number | null;
  /** The box's OTHER readers. A bridge on one of their subnets is invisible to
   *  this sweep, so the UI can offer to re-run against them. */
  other_readers?: { reader_id: number; ip_address: string; is_active: boolean }[];
  note?: string;
}

/** Broadcast-search for bridges, run on the box. Works across subnets, which
 *  is what makes it the recovery path for a reader at an unknown address.
 *
 *  The sweep is hinted at ONE reader's subnet, because that is what a directed
 *  broadcast needs. On a multi-reader box, run it once per reader. */
export async function discoverBridges(
  token: string,
  scu: string,
  readerId?: number,
): Promise<BridgeDiscovery> {
  return apiFetch<BridgeDiscovery>(
    `${base(scu)}/discover${readerQuery(readerId)}`,
    token,
  );
}

/** Re-address a bridge AND repoint the box at it — both halves of the move,
 *  because doing only one presents exactly as a dead reader.
 *
 *  `readerId` says WHICH reader is being moved. The box-side update is scoped
 *  to that one reader, so the others keep their own addresses. */
export async function readdressBridge(
  token: string,
  scu: string,
  args: { mac: string; ip: string; gateway?: string; netmask?: string },
  readerId?: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/bridge/readdress`, token, {
    method: "POST",
    body: JSON.stringify({ ...args, confirm: true, ...withReader(readerId) }),
  });
}

/** What the reader reports is physically attached, vs what the box scans. */
export interface AntennaDetection {
  scu_name: string;
  reader_id: number;
  /** Ports the reader says have an antenna attached. */
  detected: number[];
  /** Ports the box currently scans. */
  stored_active: number[];
  /** Attached but not scanned — coverage the box is missing. */
  would_activate: number[];
  /** Scanned but nothing attached — wasted time on every scan. */
  would_deactivate: number[];
  detected_count: number;
  warning?: string;
}

/** Preview ONE reader's antenna map. Changes nothing, but does pause the
 *  box's scanner for a moment, since it has to talk to the reader. */
export async function detectAntennas(
  token: string,
  scu: string,
  readerId?: number,
): Promise<AntennaDetection> {
  return apiFetch<AntennaDetection>(
    `${base(scu)}/antennas${readerQuery(readerId)}`,
    token,
  );
}

/** Write the detected map into the box's antenna table, for ONE reader.
 *  Re-detects server-side rather than trusting the preview, which may be
 *  stale. The other readers' maps are untouched. */
export async function applyAntennaMap(
  token: string,
  scu: string,
  readerId?: number,
): Promise<ReaderJob> {
  return apiFetch<ReaderJob>(`${base(scu)}/antennas`, token, {
    method: "POST",
    body: JSON.stringify({ confirm: true, ...withReader(readerId) }),
  });
}
